import React, { useState } from "react";
import { Plus, Trash2, RotateCcw, Undo2 } from "lucide-react";
import { useProject } from "../../state/ProjectContext";
import { Pill, Modal, Field } from "../ui-prim";
import { AIPanel } from "../AIPanel";
import { prompts } from "../../lib/ai";
import { pct, fK, fmt } from "../../lib/utils";
import { toast } from "../../lib/utils";

export default function Budget() {
  const { doc, mutate, readOnly } = useProject();
  const [showAdd, setShowAdd] = useState(false);
  const history = doc.budgetHistory || [];
  const canUndo = history.length > 0;
  const ta = doc.budget.reduce((s, b) => s + b.allocated, 0);
  const ts = doc.budget.reduce((s, b) => s + b.actual, 0);
  const tf = doc.budget.reduce((s, b) => s + b.forecast, 0);
  const v = tf - ta;

  const updateCell = (idx, key, val) => {
    mutate(d => ({
      ...d, budget: d.budget.map((b, i) => i === idx ? { ...b, [key]: Number(val) || 0 } : b),
    }));
  };

  const updateCategoryName = (idx, val) => {
    mutate(d => ({
      ...d, budget: d.budget.map((b, i) => i === idx ? { ...b, category: val } : b),
    }));
  };

  const removeCategory = (idx) => {
    if (!window.confirm("Remove this budget category?")) return;
    mutate(d => ({ ...d, budget: d.budget.filter((_, i) => i !== idx) }));
  };

  const addCategory = () => {
    const g = (k) => document.getElementById("nb-" + k)?.value;
    const cat = g("cat")?.trim();
    if (!cat) return toast("Category name required");
    mutate(d => ({
      ...d, budget: [...d.budget, {
        category: cat,
        allocated: Number(g("alloc")) || 0,
        actual:    Number(g("act")) || 0,
        forecast:  Number(g("fcst")) || Number(g("alloc")) || 0,
      }],
    }));
    setShowAdd(false);
  };

  const resetAllToZero = () => {
    if (!doc.budget.length) return toast("No categories to reset");
    if (!window.confirm(`Reset Budget / Spent / Forecast to 0 for all ${doc.budget.length} categories? Category names are kept. You can undo even after refreshing.`)) return;
    const snap = {
      at: new Date().toISOString(),
      action: "reset_to_zero",
      budget: doc.budget.map(b => ({ ...b })),
    };
    mutate(d => ({
      ...d,
      budget: d.budget.map(b => ({ ...b, allocated: 0, actual: 0, forecast: 0 })),
      budgetHistory: [...(d.budgetHistory || []), snap].slice(-10), // cap to last 10
    }));
    toast("Budget reset — click Undo Reset to restore (saved to project)");
  };

  const undoReset = () => {
    if (!canUndo) return;
    const last = history[history.length - 1];
    if (!window.confirm(`Undo the reset from ${new Date(last.at).toLocaleString()}? Current values will be replaced.`)) return;
    mutate(d => ({
      ...d,
      budget: last.budget.map(b => ({ ...b })),
      budgetHistory: (d.budgetHistory || []).slice(0, -1),
    }));
    toast("Budget restored from snapshot");
  };

  return (
    <>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">Finance</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">Budget & Forecast</h2>
        </div>
        <div className="flex items-center gap-2">
          <Pill tone={v <= 0 ? "green" : "red"}>{v <= 0 ? "Under budget" : "Over forecast"}</Pill>
          {!readOnly && canUndo && (
            <button className="btn btn-secondary btn-sm" onClick={undoReset} data-testid="btn-undo-budget" title={`Restore values from before the last reset · ${history.length} snapshot(s) available`}>
              <Undo2 size={12}/> Undo Reset
            </button>
          )}
          {!readOnly && (
            <button className="btn btn-ghost btn-sm" onClick={resetAllToZero} data-testid="btn-reset-budget" title="Zero out Budget, Spent, and Forecast for every category (keeps names)">
              <RotateCcw size={12}/> Reset to 0
            </button>
          )}
          {!readOnly && <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)} data-testid="btn-add-budget"><Plus size={12}/> Add Category</button>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-export-capture="budget-kpis" data-export-title="Budget · KPI Summary">
        <Tile label="Total Budget" value={fK(ta)} color="#3B82F6"/>
        <Tile label="Spent" value={fK(ts)} color="#F59E0B" sub={`${pct(ts, ta)}%`} progress={pct(ts, ta)} pc="#F59E0B"/>
        <Tile label="Forecast" value={fK(tf)} color={tf <= ta ? "#22C55E" : "#EF4444"}/>
        <Tile label="Variance" value={(v <= 0 ? "-" : "+") + fK(Math.abs(v))} color={v <= 0 ? "#22C55E" : "#EF4444"}/>
      </div>

      <div className="card p-5 mb-5" data-export-capture="budget-categories" data-export-title="Budget · By Category">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-4">By category</div>
        <div className="space-y-3.5">
          {doc.budget.map((b, idx) => {
            const sp = pct(b.actual, b.allocated);
            const fv = b.forecast - b.allocated;
            return (
              <div key={idx} className="grid grid-cols-[180px_1fr] gap-3 items-center">
                <div className="text-sm text-zinc-800 truncate">{b.category}</div>
                <div>
                  <div className="relative h-3 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bar-fill"
                         style={{ width: `${Math.min(pct(b.forecast, b.allocated), 100)}%`, background: "rgba(245,158,11,.18)" }}/>
                    <div className="absolute inset-y-0 left-0 bar-fill"
                         style={{ width: `${Math.min(sp, 100)}%`, background: "#F59E0B" }}/>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-1 font-mono text-[10px]">
                    <label className="text-zinc-500 flex items-center gap-1">Budget:
                      <input key={`a-${idx}-${b.allocated}`} type="number" className="bg-transparent text-zinc-800 w-full outline-none focus:bg-zinc-100 px-1 rounded-sm" readOnly={readOnly}
                             defaultValue={b.allocated} onBlur={(e) => updateCell(idx, "allocated", e.target.value)} />
                    </label>
                    <label className="text-amber-700 flex items-center gap-1">Spent:
                      <input key={`s-${idx}-${b.actual}`} type="number" className="bg-transparent text-amber-700 w-full outline-none focus:bg-zinc-100 px-1 rounded-sm" readOnly={readOnly}
                             defaultValue={b.actual} onBlur={(e) => updateCell(idx, "actual", e.target.value)} />
                    </label>
                    <label className={`flex items-center gap-1 ${fv <= 0 ? "text-green-700" : "text-red-700"}`}>Forecast:
                      <input key={`f-${idx}-${b.forecast}`} type="number" className="bg-transparent w-full outline-none focus:bg-zinc-100 px-1 rounded-sm" readOnly={readOnly}
                             style={{ color: fv <= 0 ? "#15803d" : "#b91c1c" }}
                             defaultValue={b.forecast} onBlur={(e) => updateCell(idx, "forecast", e.target.value)} />
                    </label>
                  </div>
                </div>
                {!readOnly && (
                  <button className="btn btn-ghost btn-sm self-start" onClick={() => removeCategory(idx)} title="Remove" data-testid={`btn-del-budget-${idx}`}>
                    <Trash2 size={11}/>
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div className="font-mono text-[10px] text-zinc-500 mt-4 italic">{readOnly ? "Read-only view — values not editable." : "Click any number or category name to edit. Changes auto-save."}</div>
      </div>

      <AIPanel title="Budget Narrative" section="budget" buildPrompt={() => prompts.budgetNarrative(doc)} buttonLabel="Generate Narrative"/>

      {/* Add Category */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Budget Category"
             footer={<><button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
                       <button className="btn btn-primary" onClick={addCategory} data-testid="btn-save-budget">Add</button></>}>
        <Field label="Category"><input id="nb-cat" className="fld" placeholder="e.g. Cloud Infrastructure" data-testid="input-budget-cat"/></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Allocated"><input id="nb-alloc" type="number" className="fld" defaultValue={0}/></Field>
          <Field label="Actual"><input id="nb-act" type="number" className="fld" defaultValue={0}/></Field>
          <Field label="Forecast"><input id="nb-fcst" type="number" className="fld" defaultValue={0}/></Field>
        </div>
      </Modal>
    </>
  );
}

function Tile({ label, value, color, sub, progress, pc }) {
  return (
    <div className="card tile-accent p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">{label}</div>
      <div className="font-display text-3xl font-extrabold mt-1 tracking-tighter" style={{ color }}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-zinc-500 mt-1">{sub}</div>}
      {progress !== undefined && (
        <div className="h-1 bg-zinc-100 rounded-sm overflow-hidden mt-2">
          <div className="h-full bar-fill" style={{ width: `${progress}%`, background: pc }}/>
        </div>
      )}
    </div>
  );
}
